import { Sparkles } from 'lucide-react';

import type {
  LabsArchetype,
  LabsAxis,
  LabsChipGroup,
  LabsDerivedMetric,
  LabsEventMap,
  LabsMindsetQuestion,
  LabsRecommendation,
  LabsSubject,
  LabsSwipeCard,
  VerticalPack,
} from '@/app/(platform)/(subscription-gated)/labs/_engine/types';
import { PACKS } from '@/app/(platform)/(subscription-gated)/labs/_packs/registry';

import { EXPLORE_COPY } from './explore-copy';

/**
 * EXPLORE — the vertical-less lab, for guide pages no vertical fits.
 *
 * 🔴 WHY THIS EXISTS. Experiment 4 can only place a lab on a page whose topic matches a track we have
 * built, which takes 395 candidate guide pages down to 74. The other 321 pages carry roughly 86,000
 * clicks a month — more than double the matched traffic — and they are nursing, marketing, engineering,
 * accounting, law. No finance lab will ever be the right thing to put in front of them, and a router
 * asking "finance, consulting or data?" is worse: a nursing reader has no good answer, so it reads as a
 * form that was not built for them.
 *
 * So this pack contrasts an axis that is real in EVERY field rather than one field's two halves.
 * Working out what is actually going on, against making the thing actually work. A nurse assesses then
 * treats; a marketer researches then runs the campaign; an engineer diagnoses then ships. The reader
 * does not need to have chosen a career for the question to mean something, which is the whole point.
 *
 * 🔴 PREVIEW-ONLY BY CONSTRUCTION, AND THAT IS A SAFETY PROPERTY, NOT A TIDINESS ONE. It is registered
 * in `../_engine/preview-packs.ts`, NOT in `_packs/registry.ts`. The shared registry feeds the gated
 * `/labs/[vertical]` route, the entry-gate precedence rules and the side-nav rail, all of which are
 * surfaces Experiment 3 is live on. A fourth entry there would change which modal a learner sees and
 * which rail items render, mid-experiment, for a pack that has no business being inside the product.
 * Keeping it out means `/try/explore` is the only route in the app that can resolve this slug.
 *
 * WHAT IS DELIBERATELY THIN. `VerticalPack` is the sprint's contract, so it demands blocks the preview
 * never reaches: M4, the hand-off modal, both offer modals, the nav rail. The preview runs nine steps
 * ending at the payoff and renders none of them. They are supplied honestly and briefly rather than
 * elaborately, and every one carries a comment saying so. Authoring four real M4 subjects for screens
 * that cannot be reached would be a few hundred lines of content nobody will ever see.
 */

// ---------------------------------------------------------------------------
// The axis
// ---------------------------------------------------------------------------

/**
 * Diagnosis against delivery. Chosen over "people vs systems" (which maps so weakly onto the catalog
 * that the archetypes stop differentiating the payoff) and over "start new things vs improve what
 * exists" (a startup framing, badly suited to someone who searched for a nursing internship).
 *
 * It is also the closest of the three to what the real verticals already contrast, so a reader who
 * finishes this and continues into finance or consulting meets a familiar shape rather than a new one.
 */
const EXPLORE_AXIS: LabsAxis = {
  bothAccent: '#7c3aed',
  poles: {
    a: { accent: '#2853d2', label: 'Diagnosis' },
    b: { accent: '#b45309', label: 'Delivery' },
  },
};

// ---------------------------------------------------------------------------
// Archetypes
// ---------------------------------------------------------------------------

const EXPLORE_ARCHETYPES: Readonly<Record<string, LabsArchetype>> = {
  builder: {
    accent: EXPLORE_AXIS.poles.b.accent,
    body: 'You care whether it actually works. The handoff that stops dropping things, the process that survives a busy week, the fix that still holds after you have moved on. Plenty of people can say what should happen. Far fewer can make it happen.',
    displayName: 'THE BUILDER',
    id: 'builder',
    tagline: 'Would rather fix the thing than describe it.',
  },
  diagnoser: {
    accent: EXPLORE_AXIS.poles.a.accent,
    body: 'You want to know what is really going on before anyone starts moving. You are the person who notices that the problem everyone is solving is not the problem they have. That instinct is worth more than it looks early on, because most wasted work starts with the wrong question.',
    displayName: 'THE DIAGNOSER',
    id: 'diagnoser',
    tagline: 'Finds the real problem before anyone moves.',
  },
  gen: {
    accent: EXPLORE_AXIS.bothAccent,
    body: 'You can work out what is wrong and then go fix it. Most people are clearly better at one half than the other, so being genuinely comfortable in both is rarer than it sounds, and it opens more doors early than either half alone.',
    displayName: 'THE OPERATOR',
    id: 'gen',
    tagline: 'Comfortable in both halves of the job.',
  },
};

// ---------------------------------------------------------------------------
// M1 — the swipe deck
// ---------------------------------------------------------------------------

/**
 * Eight activities, alternating poles so the deck never lets a reader straight-swipe one side.
 *
 * 🔴 NOT ONE OF THEM NAMES A FIELD. That is the constraint that makes this pack work on a nursing guide
 * and an engineering guide at once. Each is a recognisable thing that happens in an actual job, phrased
 * so a reader supplies their own context: "the same complaint keeps coming back" is a ward, a support
 * queue, a client account or a bug tracker depending on who is reading. Naming any of those would
 * exclude everyone else on the page.
 */
const M1_CARDS: readonly LabsSwipeCard[] = [
  { icon: '🔍', id: 'm1-1', pole: 'a', text: 'Work out why the same complaint keeps coming back' },
  { icon: '🔧', id: 'm1-2', pole: 'b', text: 'Redesign the handoff so nothing gets dropped' },
  { icon: '📊', id: 'm1-3', pole: 'a', text: 'Dig through the numbers until the real cause shows up' },
  { icon: '🚀', id: 'm1-4', pole: 'b', text: 'Get a new way of working actually adopted by the people doing it' },
  {
    icon: '🎤',
    id: 'm1-5',
    pole: 'a',
    text: 'Talk to the people closest to the problem and find what nobody wrote down',
  },
  { icon: '✅', id: 'm1-6', pole: 'b', text: 'Take something that half works and make it work every time' },
  { icon: '🧭', id: 'm1-7', pole: 'a', text: 'Decide which of five problems is the one worth solving' },
  { icon: '🤝', id: 'm1-8', pole: 'b', text: 'Train the team so the fix survives after you leave' },
];

// ---------------------------------------------------------------------------
// M2 — the mindset sliders
// ---------------------------------------------------------------------------

/**
 * Four situations, each with two REASONABLE instincts. The failure mode of a mindset quiz is one
 * obviously-correct end; both anchors here are things a good colleague genuinely does.
 *
 * `scoredSide` is 'left' on every question because `deriveArchetype` reads the raw slider value (0-2 =
 * diagnosis). A right-scored question would need the engine to invert the value, which it does not do,
 * so the diagnosis anchor must always sit on the left.
 */
const M2_QUESTIONS: readonly LabsMindsetQuestion[] = [
  {
    id: 'q1',
    leftLabel: 'Find out what is actually happening',
    q: 'Something is going wrong and nobody agrees why. What is your first move?',
    rightLabel: 'Start fixing the most obvious blocker',
    scoredSide: 'left',
  },
  {
    id: 'q2',
    leftLabel: 'Work out which problem costs the most',
    q: 'You have a free week and nobody has asked you for anything. What do you do?',
    rightLabel: 'Fix the thing that annoys everyone daily',
    scoredSide: 'left',
  },
  {
    id: 'q3',
    leftLabel: 'Question whether it was the right problem',
    q: 'A change you made last month has not stuck.',
    rightLabel: 'Question how it was rolled out',
    scoredSide: 'left',
  },
  {
    // The AI question, asked in every pack. It is the one a student in 2026 is actually worried about,
    // and it splits the poles honestly rather than flattering either.
    id: 'q4',
    leftLabel: 'Knowing which question to ask',
    q: 'An AI drafts the whole analysis in four seconds. Where does your value sit?',
    rightLabel: 'Getting it to actually happen',
    scoredSide: 'left',
  },
];

/** Slider midpoint. Below is the diagnosis pole, above is delivery, exactly on it is neither. */
const SLIDER_CENTRE = 3;

function deriveArchetype(answers: Readonly<Record<string, number>>): string {
  let diagnosis = 0;
  let delivery = 0;

  for (const value of Object.values(answers)) {
    if (value < SLIDER_CENTRE) {
      diagnosis += 1;
    } else if (value > SLIDER_CENTRE) {
      delivery += 1;
    }
  }

  if (diagnosis === delivery) {
    return 'gen';
  }
  return diagnosis > delivery ? 'diagnoser' : 'builder';
}

// ---------------------------------------------------------------------------
// M3 — the practice subject and the three rates
// ---------------------------------------------------------------------------

/**
 * Northwind Support — the fictional practice operation (finance's NovaTech, consulting's Swiftcart).
 * Fictional on purpose: the numbers are built to teach ONE lesson cleanly, which no real table does.
 *
 * The lesson: volume more than triples (+217%) while every quality gauge decays in lockstep. First-time
 * resolution 90.0% → 84.8% → 80.0%, repeat contacts 8% → 10% → 12%, escalations 3.0% → 4.0% → 5.0%. The
 * operation is not straining under growth, it is being outrun by it.
 *
 * A support queue was chosen as the subject because every reader has been on one end of one. It needs
 * no domain knowledge to read, which is the same requirement the M1 deck has.
 *
 * The arithmetic is deliberately the same shape as the consulting pack's practice table. It is a
 * teaching device in both, the three distractors are the same three real mis-derivations, and diverging
 * the numbers for the sake of it would only risk introducing an error into a table whose whole job is
 * to be correct.
 */
const M3_SUBJECT: LabsSubject = {
  accent: EXPLORE_AXIS.poles.b.accent,
  id: 'northwind',
  logo: '',
  monogram: 'N',
  name: 'Northwind Support',
  periods: ['2022', '2023', '2024'],
  rows: [
    { key: 'requests', label: 'Requests', values: [120, 210, 380] },
    { emphasis: 'subtotal', key: 'firstTime', label: 'Solved First Time', values: [108, 178, 304] },
    { key: 'repeats', label: 'Repeat Contacts', values: [9.6, 21, 45.6] },
    { emphasis: 'subtotal', key: 'escalations', label: 'Escalations', values: [3.6, 8.4, 19] },
  ],
  subtitle: '',
  tagline: 'Practice example',
  tags: [],
  unit: { prefix: '', suffix: 'k' },
};

/**
 * The three practice rates. Each wrong option is a REAL mis-derivation a beginner makes, not a random
 * near-miss: dividing by the wrong base, reading the wrong year's column, or computing a different
 * row's rate by mistake. Options are stored canonically; the engine shuffles their positions.
 */
const M3_METRICS: readonly LabsDerivedMetric[] = [
  {
    correct: 216.7,
    formula: '(380 − 120) ÷ 120 × 100',
    key: 'requestGrowth',
    name: 'Request Growth',
    // 81.0 = the 2024-only leg (380 ÷ 210); 68.4 = dividing the change by the END year, not the base.
    options: [68.4, 81.0, 216.7],
    period: '(2 year)',
    sources: [
      { periodIndex: 0, row: 'requests' },
      { periodIndex: 2, row: 'requests' },
    ],
  },
  {
    correct: 80.0,
    formula: '304 ÷ 380 × 100',
    key: 'firstTimeRate',
    name: 'First-Time Resolution',
    // 90.0 = 2022's rate (right formula, wrong column); 84.8 = 2023's (same mistake, other column).
    options: [80.0, 84.8, 90.0],
    period: '(2024)',
    sources: [
      { periodIndex: 2, row: 'firstTime' },
      { periodIndex: 2, row: 'requests' },
    ],
  },
  {
    correct: 5.0,
    formula: '19 ÷ 380 × 100',
    key: 'escalationRate',
    name: 'Escalation Rate',
    // 3.0 = 2022's escalation rate (wrong column); 12.0 = the REPEAT rate (wrong numerator row).
    options: [3.0, 5.0, 12.0],
    period: '(2024)',
    sources: [
      { periodIndex: 2, row: 'escalations' },
      { periodIndex: 2, row: 'requests' },
    ],
  },
];

/** ±tolerance on a picked rate, so a rounding difference still counts as right (every pack uses 1.0). */
const METRIC_TOLERANCE = 1.0;

/** Every option on the M3 rate picker is a percentage. */
const METRIC_UNIT_SUFFIX = '%';

// ---------------------------------------------------------------------------
// M4 — required by the contract, unreachable on this surface
// ---------------------------------------------------------------------------

/**
 * 🔴 THE PREVIEW NEVER RENDERS M4. `PREVIEW_STEPS` ends at `m3-activity` then the payoff, and this pack
 * is registered only for the preview, so no reader can reach any of the four M4 screens.
 *
 * Supplied because `VerticalPack` requires it, and supplied HONESTLY rather than as a stub that lies:
 * the subject is the practice table the reader has already seen, the graded group has a genuinely
 * correct answer, and the interpretive group is marked ungraded. If this pack is ever promoted to the
 * gated sprint, this block is the first thing that needs real authoring, not the last.
 */
const M4_GROUPS: readonly LabsChipGroup[] = [
  {
    chips: [
      { id: 'growth', label: 'Demand is growing fast' },
      { id: 'shrinking', label: 'Demand is shrinking' },
      { id: 'flat', label: 'Demand is flat' },
    ],
    graded: true,
    id: 'demand',
    label: 'What is happening to demand?',
  },
  {
    chips: [
      { id: 'capacity', label: 'They need more people' },
      { id: 'process', label: 'The process needs rebuilding' },
      { id: 'triage', label: 'They are solving the wrong requests first' },
    ],
    graded: false,
    id: 'diagnosis',
    label: 'What would you do about it?',
  },
];

const M4_SUBJECTS: Readonly<Record<string, LabsSubject>> = { northwind: M3_SUBJECT };

// ---------------------------------------------------------------------------
// Recommendations — drawn from the live packs, never invented
// ---------------------------------------------------------------------------

/**
 * 🔴 EVERY PROGRAM ON THE PAYOFF IS A REAL ONE, BECAUSE THEY ARE THE OTHER PACKS' OWN CURATED CARDS.
 *
 * A fallback card naming a program that does not exist is a mis-sale, and this pack has no catalog of
 * its own to draw from: "explore" is not a category any externship carries. So rather than author a
 * fresh list (which would be invention, and would rot silently as the catalog changed), each archetype
 * inherits the matching archetype's cards from finance, data and consulting.
 *
 * The mapping is by POLE, which is the thing the three axes have in common:
 *   diagnoser ← the analysis-leaning archetype of each pack (invest / translator / strategist)
 *   builder   ← the delivery-leaning one (corp / builder / operator)
 *   gen       ← each pack's own both-poles archetype
 *
 * A reader therefore ends on three programs from three different fields that suit how they think, which
 * is exactly the right payoff for someone who arrived on a page we have no vertical for.
 */
const POLE_SOURCE_ARCHETYPES = {
  builder: { consulting: 'operator', 'data-analytics': 'builder', finance: 'corp' },
  diagnoser: { consulting: 'strategist', 'data-analytics': 'translator', finance: 'invest' },
  gen: { consulting: 'gen', 'data-analytics': 'gen', finance: 'gen' },
} as const;

/** One card from each live pack for this archetype, so the three fields are all represented. */
function blendedFallback(archetype: keyof typeof POLE_SOURCE_ARCHETYPES): readonly LabsRecommendation[] {
  const sources = POLE_SOURCE_ARCHETYPES[archetype];

  return Object.entries(sources).flatMap(([slug, sourceArchetype]) => {
    const pack = PACKS[slug as keyof typeof PACKS];
    const cards = pack?.recommendations.fallback[sourceArchetype] ?? [];
    return cards.slice(0, 1);
  });
}

const RECOMMENDATIONS: Readonly<Record<string, readonly LabsRecommendation[]>> = {
  builder: blendedFallback('builder'),
  diagnoser: blendedFallback('diagnoser'),
  gen: blendedFallback('gen'),
};

/** Membership: everything the three live packs claim, since this pack is deliberately field-less. */
const EXPLORE_CATEGORIES: readonly string[] = Array.from(
  new Set(Object.values(PACKS).flatMap((pack) => pack.recommendations.categories)),
);

/**
 * Ordering within that pool, blended the same way as the fallbacks. A priority list matching EVERY
 * program is worse than none at all, so each archetype inherits only the priority terms of the matching
 * archetype in each pack rather than the union of all of them.
 */
const ARCHETYPE_PRIORITY: Readonly<Record<string, readonly string[]>> = Object.fromEntries(
  Object.entries(POLE_SOURCE_ARCHETYPES).map(([archetype, sources]) => [
    archetype,
    Array.from(
      new Set(
        Object.entries(sources).flatMap(([slug, sourceArchetype]) => [
          ...(PACKS[slug as keyof typeof PACKS]?.recommendations.archetypePriority[sourceArchetype] ?? []),
        ]),
      ),
    ),
  ]),
);

// ---------------------------------------------------------------------------
// Events — declared, never emitted
// ---------------------------------------------------------------------------

/**
 * 🔴 NOTHING EMITS THESE. The preview tracks through `LABS_PREVIEW_EVENTS` (`labs_preview_*`), which is
 * the only vocabulary this surface uses; `VerticalPack` requires an event map, so one exists.
 *
 * Named `labs_explore_*` rather than `explore_discovery_*` deliberately. `*_discovery_*` IS the
 * Experiment 3 vocabulary, and a dormant set of names that pattern-match a live experiment's is exactly
 * the sort of thing a future funnel picks up by accident.
 */
const EXPLORE_EVENTS: LabsEventMap = {
  ARCHETYPE_REVEALED: 'labs_explore_archetype_revealed',
  ELIGIBILITY_RESOLVED: 'labs_explore_eligibility_resolved',
  ENTERED: 'labs_explore_entered',
  FLOW_COMPLETED: 'labs_explore_flow_completed',
  HANDOFF_DISMISSED: 'labs_explore_handoff_dismissed',
  HANDOFF_SHOWN: 'labs_explore_handoff_shown',
  HANDOFF_TRIAL_CLICKED: 'labs_explore_handoff_trial_clicked',
  MODULE_COMPLETED: 'labs_explore_module_completed',
  OFFER_DISMISSED: 'labs_explore_offer_dismissed',
  OFFER_SHOWN: 'labs_explore_offer_shown',
  OFFER_STARTED: 'labs_explore_offer_started',
  PREPAYWALL_ELIGIBILITY_RESOLVED: 'labs_explore_prepaywall_eligibility_resolved',
  RECOMMENDATION_CLICKED: 'labs_explore_recommendation_clicked',
  STEP_VIEWED: 'labs_explore_step_viewed',
  TRIAL_BAR_CLICKED: 'labs_explore_trial_bar_clicked',
  TRIAL_BAR_SHOWN: 'labs_explore_trial_bar_shown',
};

// ---------------------------------------------------------------------------
// The pack
// ---------------------------------------------------------------------------

export const EXPLORE_PACK: VerticalPack = {
  archetypes: EXPLORE_ARCHETYPES,
  axis: EXPLORE_AXIS,
  // 🔴 MATCHES NO APPLICATION OPTION, ON PURPOSE. This value decides who is shown a vertical's entry
  // offer inside the product. There is no "explore" career focus and there must not be one: the offer
  // machinery lives on gated surfaces this pack is deliberately absent from, and a value that matched
  // something real would be the one way it could leak in.
  careerFocusArea: '__explore_never_matches__',
  copy: EXPLORE_COPY,
  deriveArchetype,
  displayName: 'Career Explorer',
  events: EXPLORE_EVENTS,
  // Never read. The preview reads no flags at all (an anonymous reader has no arm to resolve), but the
  // field is typed against the flag registry so it has to name a real key.
  featureFlag: 'ff_labs_explore_q3_2026',
  m1: { cards: M1_CARDS },
  m2: { questions: M2_QUESTIONS },
  m3: {
    metrics: M3_METRICS,
    subject: M3_SUBJECT,
    tolerance: METRIC_TOLERANCE,
    unitSuffix: METRIC_UNIT_SUFFIX,
  },
  m4: {
    answerKey: { northwind: { demand: 'growth' } },
    groups: M4_GROUPS,
    sampleAnalyses: {
      northwind:
        'Requests more than tripled in two years while first-time resolution fell from 90% to 80%. Every extra request that is not solved on the first try comes back, which is why repeat contacts rose faster than volume did. The queue is not short of effort, it is short of a way to handle the requests it now gets.',
    },
    subjects: M4_SUBJECTS,
    takeSamples: {
      _default: 'Adding people would clear the backlog for a quarter. It would not stop the same requests coming back.',
    },
  },
  // Unreachable: the rail only renders inside the gated product, where this pack is not registered.
  nav: { icon: Sparkles, label: 'Career Explorer', mobileLabel: 'Explore' },
  recommendations: {
    archetypePriority: ARCHETYPE_PRIORITY,
    categories: EXPLORE_CATEGORIES,
    fallback: RECOMMENDATIONS,
  },
  slug: 'explore',
};
